<script setup lang="ts">
type Zone =
  | 'top-left'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left';

const ZONES: readonly Zone[] = [
  'top-left',
  'top-right',
  'right',
  'bottom-right',
  'bottom',
  'bottom-left',
];

interface Props {
  buttonShadows?: boolean;
  classes?: {
    container?: string;
    inner?: string;
    content?: string;
  };
  customFontFaceCssCode?: string | null;
  isCentered?: boolean;
  isFullscreen?: boolean;
  isStepContentHidden?: boolean;
}

withDefaults(defineProps<Props>(), {
  buttonShadows: false,
  classes: undefined,
  customFontFaceCssCode: null,
  isCentered: false,
  isFullscreen: false,
  isStepContentHidden: false,
});

defineSlots<
  {
    default?: () => unknown;
    KioskShell__auxiliary?: () => unknown;
  } & {
    [Zone in (typeof ZONES)[number] as `KioskLayout__${Zone}`]?: () => unknown;
  }
>();
</script>

<template>
  <div
    class="KioskLayout"
    data-testid="kiosk-shell"
    :class="[{
      'KioskLayout--centered': isCentered,
      'KioskLayout--fullscreen': isFullscreen,
      'kiosk--button_shadows': buttonShadows,
    }, classes?.container]"
  >
    <div
      class="KioskLayout__inner"
      data-testid="kiosk-shell-inner"
      :class="classes?.inner"
    >
      <div
        class="KioskLayout__content"
        data-testid="kiosk-shell-content"
        :class="[{ 'd-none': isStepContentHidden }, classes?.content]"
      >
        <slot/>
      </div>
    </div>

    <div
      v-for="zone in ZONES"
      :key="zone"
      :class="`KioskLayout__${zone}`"
      :data-testid="`kiosk-shell-${zone}`"
    >
      <slot :name="`KioskLayout__${zone}`"/>
    </div>

    <slot name="KioskShell__auxiliary"/>

    <div
      v-if="customFontFaceCssCode"
      class="KioskLayout__custom-font-css"
      v-html="customFontFaceCssCode"
    />
  </div>
</template>

<style scoped lang="scss">
  .KioskLayout {
    display: grid;
    grid-template-areas: 'stage';
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
    color: var(--kiosk-color-text);
    font-family: var(--kiosk-font-family-heading);
    font-size: var(--kiosk-font-scale);
    text-shadow:
      0
      calc(0.5em * var(--kiosk-text-shadow-size))
      calc(1.5em * var(--kiosk-text-shadow-size))
      rgba(var(--kiosk-color-tint-rgb), var(--kiosk-text-shadow-opacity));
    background: {
      color: var(--kiosk-color-surface);
      image: var(--kiosk-background-image);
      size: cover;
      position: center;
      repeat: no-repeat;
    };

    :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6), :deep(b), :deep(strong) {
      font-weight: normal;                  // All custom fonts always load as non-bold & non-italic
      font-variation-settings: 'wght' 480;  // BUT if VARIABLE fonts are passed, then apply weight
    }

    :deep(i), :deep(em) {
      font-style: normal;
    }

    :deep(a:not(.kiosk-button):not(.kiosk-button-inline)) {
      color: var(--kiosk-color-text);
      text-underline-offset: 12%;
      transition: transform 50ms;

      &:active {
        transform: scale(0.96) translateY(0.11rem);
      }
    }

    &--centered {
      .KioskLayout__inner {
        align-items: center;
      }
    }

    &--fullscreen {
      .KioskLayout__inner {
        align-items: stretch;
        justify-content: stretch;
      }
      .KioskLayout__content {
        padding: 0;
        width: 100%;
      }
    }
  }

  .KioskLayout__inner {
    grid-area: stage;
    min-height: var(--Global--height-landscape);
    overflow: hidden;
    display: flex;
    align-items: stretch;
    justify-content: center;
  }

  .KioskLayout__content {
    padding:
      var(--kiosk-shell-gap-top)
      var(--kiosk-shell-gap-inline)
      var(--kiosk-shell-gap-bottom)
      var(--kiosk-shell-gap-inline);
  }

  .KioskLayout__top-left,
  .KioskLayout__top-right,
  .KioskLayout__right,
  .KioskLayout__bottom-right,
  .KioskLayout__bottom,
  .KioskLayout__bottom-left {
    position: relative;
    grid-area: stage;
  }

  .KioskLayout__top-left {
    align-self: start;
    justify-self: start;
    z-index: 22;
  }

  .KioskLayout__top-right {
    align-self: start;
    justify-self: end;
    z-index: 22;
  }

  .KioskLayout__right {
    align-self: start;
    justify-self: end;
    z-index: 20;
  }

  .KioskLayout__bottom-right {
    align-self: end;
    justify-self: end;
    z-index: 21;
  }

  .KioskLayout__bottom {
    align-self: end;
    justify-self: center;
    min-width: 0;
    max-width: 100%;
    z-index: 20;
  }

  .KioskLayout__bottom-left {
    align-self: end;
    justify-self: start;
    z-index: 21;
  }

  .KioskLayout__custom-font-css {
    position: absolute;
  }

  :deep(.KioskLayout__print-lock-trigger) {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 6rem;
    height: 6rem;
    z-index: 25;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.KioskLayout__diagnostics-trigger) {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 6rem;
    height: 6rem;
    z-index: 25;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
